import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { Navigation } from '../components/Navigation';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, Line
} from 'recharts';

const formatDKK = (v) => {
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return v?.toLocaleString('da-DK') ?? '0';
};

// Reusable Gauge component
function Gauge({ value, label, color, size = 160, maxValue = 100 }) {
  const pct = Math.min(Math.max((value / maxValue) * 100, 0), 100);
  const radius = (size - 16) / 2;
  const cx = size / 2;
  const cy = size / 2 + 8;
  const startAngle = Math.PI;
  const endAngle = 0;
  const totalAngle = startAngle - endAngle;
  const valueAngle = startAngle - (pct / 100) * totalAngle;
  
  const arcPath = (start, end, r) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy - r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy - r * Math.sin(end);
    const largeArc = start - end > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const needleX = cx + (radius - 12) * Math.cos(valueAngle);
  const needleY = cy - (radius - 12) * Math.sin(valueAngle);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 24} viewBox={`0 0 ${size} ${size / 2 + 24}`}>
        <path d={arcPath(startAngle, endAngle, radius)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" strokeLinecap="round" />
        {pct > 0 && (
          <path d={arcPath(startAngle, valueAngle, radius)} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" />
        )}
        <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke="#fff" strokeWidth="2" />
        <circle cx={cx} cy={cy} r="3" fill="#fff" />
        <text x={cx} y={cy - 12} textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700">{value.toFixed(1)}%</text>
        <text x={cx} y={cy + 2} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="9">{label}</text>
      </svg>
    </div>
  );
}

// Category Card Component with toggleable periods and trend
const PERIODS = [
  { label: '1 md', months: 1 },
  { label: '3 md', months: 3 },
  { label: '6 md', months: 6 },
  { label: '12 md', months: 12 },
];

const DB_PERIODS = [
  { label: 'Sidste måned', months: 1, color: '#10B981' },
  { label: 'Sidste 3 måneder', months: 3, color: '#1E88E5' },
  { label: 'Sidste 6 måneder', months: 6, color: '#7B1FA2' },
  { label: 'Sidste 12 måneder', months: 12, color: '#E65100' },
];

function CategoryCard({ title, dataKey, rawData, color, icon }) {
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [showTrend, setShowTrend] = useState(false);
  const [showAsDBPct, setShowAsDBPct] = useState(false); // false = % af omsætning, true = % af DB

  const stats = useMemo(() => {
    if (!rawData.length) return null;
    const completedMonths = rawData.slice(0, -1);
    if (!completedMonths.length) return null;

    const p = PERIODS[selectedPeriod];
    const slice = completedMonths.slice(-p.months);
    const totalOms = slice.reduce((s, d) => s + d.omsaetning, 0);
    const totalDB = slice.reduce((s, d) => s + d.daekningsbidrag, 0);
    const totalValue = slice.reduce((s, d) => s + (d[dataKey] || 0), 0);
    const pctOfOms = totalOms > 0 ? (totalValue / totalOms) * 100 : 0;
    const pctOfDB = totalDB > 0 ? (totalValue / totalDB) * 100 : 0;
    const avgPerMonth = totalValue / slice.length;

    return { pctOfOms, pctOfDB, totalValue, totalOms, totalDB, avgPerMonth, monthCount: slice.length };
  }, [rawData, selectedPeriod, dataKey]);

  const trendData = useMemo(() => {
    if (!rawData.length) return [];
    const completedMonths = rawData.slice(0, -1);
    return completedMonths.slice(-6).map(d => {
      const base = showAsDBPct ? d.daekningsbidrag : d.omsaetning;
      return {
        month: d.month,
        pct: base > 0 ? Math.round((d[dataKey] / base) * 1000) / 10 : 0,
      };
    });
  }, [rawData, dataKey, showAsDBPct]);

  if (!stats) return null;

  const currentPct = showAsDBPct ? stats.pctOfDB : stats.pctOfOms;
  const baseLabel = showAsDBPct ? 'DB' : 'oms.';

  return (
    <div className="bg-[#151515] rounded-xl p-4 border border-white/5" data-testid={`category-${dataKey}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">{icon}</span>
        <h3 className="text-white text-sm font-semibold">{title}</h3>
      </div>
      
      {/* Toggle: % af omsætning / % af DB */}
      <div className="flex justify-center mb-2">
        <div className="flex bg-[#0A0A0A] rounded-lg p-0.5">
          <button
            onClick={() => setShowAsDBPct(false)}
            className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
              !showAsDBPct ? 'text-white bg-white/15' : 'text-white/40 hover:text-white/70'
            }`}
          >
            % af oms.
          </button>
          <button
            onClick={() => setShowAsDBPct(true)}
            className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
              showAsDBPct ? 'text-white bg-white/15' : 'text-white/40 hover:text-white/70'
            }`}
          >
            % af DB
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex justify-center mb-3">
        <div className="flex gap-1 bg-[#0A0A0A] rounded-lg p-1">
          {PERIODS.map((p, i) => (
            <button
              key={i}
              onClick={() => { setSelectedPeriod(i); setShowTrend(false); }}
              className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                selectedPeriod === i && !showTrend
                  ? 'text-white' 
                  : 'text-white/40 hover:text-white/70'
              }`}
              style={selectedPeriod === i && !showTrend ? { backgroundColor: color } : {}}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setShowTrend(!showTrend)}
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              showTrend ? 'text-white bg-white/15' : 'text-white/40 hover:text-white/70'
            }`}
          >
            Tendens
          </button>
        </div>
      </div>

      {showTrend ? (
        <div className="h-[180px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trendData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 9 }} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 9 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '11px' }}
                formatter={(value) => [`${value.toFixed(1)}%`, `% af ${baseLabel}`]}
              />
              <Line type="monotone" dataKey="pct" stroke={color} strokeWidth={2} dot={{ fill: color, r: 3, strokeWidth: 0 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <Gauge value={currentPct} label={`${PERIODS[selectedPeriod].label} (${baseLabel})`} color={color} size={140} maxValue={Math.max(currentPct * 1.5, 50)} />
          <div className="mt-2 text-center">
            <p className="text-white/40 text-xs">Total: {formatDKK(Math.round(stats.totalValue))} kr</p>
            <p className="text-white/40 text-xs">Gns/md: {formatDKK(Math.round(stats.avgPerMonth))} kr</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Main DB Section (larger, more prominent)
function DBSection({ rawData }) {
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [showTrend, setShowTrend] = useState(false);

  const stats = useMemo(() => {
    if (!rawData.length) return null;
    const completedMonths = rawData.slice(0, -1);
    if (!completedMonths.length) return null;

    const p = DB_PERIODS[selectedPeriod];
    const slice = completedMonths.slice(-p.months);
    const totalOms = slice.reduce((s, d) => s + d.omsaetning, 0);
    const totalDB = slice.reduce((s, d) => s + d.daekningsbidrag, 0);
    const dbPct = totalOms > 0 ? (totalDB / totalOms) * 100 : 0;
    const avgOms = totalOms / slice.length;
    const avgDB = totalDB / slice.length;

    return { dbPct, totalOms, totalDB, avgOms, avgDB, color: p.color, label: p.label };
  }, [rawData, selectedPeriod]);

  const trendData = useMemo(() => {
    if (!rawData.length) return [];
    const completedMonths = rawData.slice(0, -1);
    return completedMonths.slice(-6).map(d => ({
      month: d.month,
      db_pct: d.omsaetning > 0 ? Math.round((d.daekningsbidrag / d.omsaetning) * 1000) / 10 : 0,
    }));
  }, [rawData]);

  if (!stats) return null;

  return (
    <div className="bg-[#151515] rounded-xl p-6 border border-white/5" data-testid="db-gauge-combined">
      <h3 className="text-white text-base font-semibold mb-3 text-center">DB i % af omsætning</h3>
      <div className="flex items-center justify-center mb-4">
        <div className="flex gap-1 bg-[#0A0A0A] rounded-lg p-1" data-testid="period-selector">
          {DB_PERIODS.map((ps, i) => (
            <button
              key={i}
              onClick={() => { setSelectedPeriod(i); setShowTrend(false); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                selectedPeriod === i && !showTrend
                  ? 'text-white' 
                  : 'text-white/40 hover:text-white/70'
              }`}
              style={selectedPeriod === i && !showTrend ? { backgroundColor: ps.color } : {}}
              data-testid={`period-btn-${ps.months}m`}
            >
              {ps.months} md
            </button>
          ))}
          <button
            onClick={() => setShowTrend(!showTrend)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              showTrend ? 'text-white bg-white/15' : 'text-white/40 hover:text-white/70'
            }`}
            data-testid="trend-btn"
          >
            Tendens
          </button>
        </div>
      </div>
      {showTrend ? (
        <div className="flex justify-center items-center mt-2 w-full" data-testid="trend-chart">
          <div className="h-[250px] w-full max-w-md mx-auto">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} domain={['auto', 'auto']} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                  formatter={(value) => [`${value.toFixed(1)}%`, 'DB%']}
                />
                <Line type="monotone" dataKey="db_pct" stroke="#ffffff" strokeWidth={2.5} dot={{ fill: '#fff', r: 4, strokeWidth: 0 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <Gauge value={stats.dbPct} label={stats.label} color={stats.color} size={200} maxValue={100} />
          <div className="mt-4 grid grid-cols-2 gap-4 w-full max-w-xs">
            <div className="text-center">
              <p className="text-white/40 text-xs">Omsætning</p>
              <p className="text-white text-sm font-semibold">{formatDKK(Math.round(stats.totalOms))} kr</p>
            </div>
            <div className="text-center">
              <p className="text-white/40 text-xs">Dækningsbidrag</p>
              <p className="text-sm font-semibold" style={{ color: stats.color }}>{formatDKK(Math.round(stats.totalDB))} kr</p>
            </div>
            <div className="text-center">
              <p className="text-white/40 text-xs">Gns. oms./md</p>
              <p className="text-white text-xs font-semibold">{formatDKK(Math.round(stats.avgOms))} kr</p>
            </div>
            <div className="text-center">
              <p className="text-white/40 text-xs">Gns. DB/md</p>
              <p className="text-xs font-semibold" style={{ color: stats.color }}>{formatDKK(Math.round(stats.avgDB))} kr</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Break-Even Section - shows avg monthly revenue/DB vs expenses
function BreakEvenSection({ rawData }) {
  const [compareWithDB, setCompareWithDB] = useState(false); // false = omsætning, true = DB

  const stats = useMemo(() => {
    if (!rawData.length) return null;
    const completedMonths = rawData.slice(0, -1);
    if (!completedMonths.length) return null;

    const periods = [
      { label: '1 md', months: 1, color: '#10B981' },
      { label: '3 md', months: 3, color: '#1E88E5' },
      { label: '6 md', months: 6, color: '#7B1FA2' },
      { label: '12 md', months: 12, color: '#E65100' },
    ];

    return periods.map(p => {
      const slice = completedMonths.slice(-p.months);
      const totalOms = slice.reduce((s, d) => s + d.omsaetning, 0);
      const totalDB = slice.reduce((s, d) => s + d.daekningsbidrag, 0);
      const totalOmk = slice.reduce((s, d) => s + d.samlede_omkostninger, 0);
      const avgOms = totalOms / slice.length;
      const avgDB = totalDB / slice.length;
      const avgOmk = totalOmk / slice.length;
      
      // Calculate margins for both comparison modes
      const marginVsOms = avgOms - avgOmk;
      const marginVsDB = avgDB - avgOmk;
      const breakEvenPctOms = avgOms > 0 ? (avgOmk / avgOms) * 100 : 0;
      const breakEvenPctDB = avgDB > 0 ? (avgOmk / avgDB) * 100 : 0;

      return {
        ...p,
        avgOms,
        avgDB,
        avgOmk,
        marginVsOms,
        marginVsDB,
        breakEvenPctOms,
        breakEvenPctDB,
        monthCount: slice.length,
      };
    });
  }, [rawData]);

  if (!stats) return null;

  // Get the comparison base value based on toggle
  const getBaseValue = (s) => compareWithDB ? s.avgDB : s.avgOms;
  const getMargin = (s) => compareWithDB ? s.marginVsDB : s.marginVsOms;
  const getBreakEvenPct = (s) => compareWithDB ? s.breakEvenPctDB : s.breakEvenPctOms;
  const baseLabel = compareWithDB ? 'DB' : 'Omsætning';
  const baseLabelShort = compareWithDB ? 'DB' : 'Oms.';

  // Find max value for scaling the bars
  const maxAvg = Math.max(...stats.map(s => Math.max(getBaseValue(s), s.avgOmk)));

  return (
    <div className="bg-[#151515] rounded-xl p-6 border border-white/5" data-testid="break-even-section">
      <h3 className="text-white text-base font-semibold mb-2 text-center">Break-Even Analyse</h3>
      <p className="text-white/40 text-xs text-center mb-4">Gns. månedlig {baseLabel.toLowerCase()} vs. udgifter (ekskl. indeværende måned)</p>
      
      {/* Toggle: Omsætning / DB */}
      <div className="flex justify-center mb-6">
        <div className="flex bg-[#0A0A0A] rounded-lg p-1">
          <button
            onClick={() => setCompareWithDB(false)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              !compareWithDB ? 'text-white bg-white/15' : 'text-white/40 hover:text-white/70'
            }`}
          >
            vs. Omsætning
          </button>
          <button
            onClick={() => setCompareWithDB(true)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              compareWithDB ? 'text-white bg-white/15' : 'text-white/40 hover:text-white/70'
            }`}
          >
            vs. DB
          </button>
        </div>
      </div>
      
      <div className="space-y-6">
        {stats.map((s, idx) => {
          const baseValue = getBaseValue(s);
          const margin = getMargin(s);
          const breakEvenPct = getBreakEvenPct(s);
          const baseWidth = maxAvg > 0 ? (baseValue / maxAvg) * 100 : 0;
          const omkWidth = maxAvg > 0 ? (s.avgOmk / maxAvg) * 100 : 0;
          const isPositive = margin > 0;
          
          return (
            <div key={idx} className="space-y-2">
              {/* Period label */}
              <div className="flex items-center justify-between">
                <span className="text-white text-sm font-medium">{s.label}</span>
                <span className={`text-xs font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {isPositive ? '+' : ''}{formatDKK(Math.round(margin))} kr/md
                </span>
              </div>
              
              {/* Visual bars */}
              <div className="space-y-1.5">
                {/* Base value bar (Omsætning or DB) */}
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-[10px] w-12">{baseLabelShort}</span>
                  <div className="flex-1 h-6 bg-[#0A0A0A] rounded-full overflow-hidden relative">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${baseWidth}%`, backgroundColor: s.color }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-[10px] font-medium">
                      {formatDKK(Math.round(baseValue))} kr
                    </span>
                  </div>
                </div>
                
                {/* Udgifter bar */}
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-[10px] w-12">Udg.</span>
                  <div className="flex-1 h-6 bg-[#0A0A0A] rounded-full overflow-hidden relative">
                    <div 
                      className="h-full rounded-full transition-all duration-500 bg-white/20"
                      style={{ width: `${omkWidth}%` }}
                    />
                    {/* Break-even line indicator */}
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500"
                      style={{ left: `${baseWidth}%` }}
                      title="Break-even punkt"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-[10px] font-medium">
                      {formatDKK(Math.round(s.avgOmk))} kr
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Break-even percentage indicator */}
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-white/40">
                  Udgifter udgør <span className="text-white font-medium">{breakEvenPct.toFixed(1)}%</span> af {baseLabel.toLowerCase()}
                </span>
                <span className={`font-medium ${breakEvenPct < 90 ? 'text-green-400' : breakEvenPct < 100 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {breakEvenPct < 100 ? `${(100 - breakEvenPct).toFixed(1)}% margin` : 'Over break-even!'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-center gap-6 text-[10px]">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#7B1FA2]" />
          <span className="text-white/50">{baseLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-white/20" />
          <span className="text-white/50">Udgifter</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-red-500" />
          <span className="text-white/50">Break-even</span>
        </div>
      </div>
    </div>
  );
}

export default function OkonomiskAnalyse() {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/saldobalance/omsaetning');
        const allData = res.data.data || [];
        const data = allData.filter(d => d.omsaetning > 0);
        setRawData(data);
      } catch (err) {
        console.error('Error fetching saldobalance:', err);
        setError(err?.response?.data?.detail || err.message || 'Kunne ikke hente data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Define all expense categories
  const expenseCategories = [
    { title: 'Lønninger', dataKey: 'loenninger', color: '#EF4444', icon: '👥' },
    { title: 'Farveprøver', dataKey: 'farveproever', color: '#F59E0B', icon: '🎨' },
    { title: 'Rejse- og mødeomk.', dataKey: 'rejse_moede', color: '#10B981', icon: '✈️' },
    { title: 'Salgsomkostninger', dataKey: 'salgsomkostninger', color: '#3B82F6', icon: '📊' },
    { title: 'Autodrift', dataKey: 'autodrift', color: '#8B5CF6', icon: '🚗' },
    { title: 'Lokaleomkostninger', dataKey: 'lokaleomkostninger', color: '#EC4899', icon: '🏢' },
    { title: 'Software', dataKey: 'software', color: '#06B6D4', icon: '💻' },
    { title: 'Adm. omkostninger', dataKey: 'admin_omk', color: '#F97316', icon: '📋' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/50" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold mb-4">Økonomisk analyse</h1>
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
            <p className="text-red-400">Fejl: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!rawData.length) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold mb-4">Økonomisk analyse</h1>
          <div className="bg-[#151515] rounded-xl p-8 text-center border border-white/5">
            <p className="text-white/40">Ingen data tilgængelig fra Saldo_Data_Klar</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 md:p-6" data-testid="okonomisk-analyse-page">
      <Navigation />
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl md:text-2xl font-bold">Økonomisk analyse</h1>
        </div>

        {/* Main DB Section */}
        <DBSection rawData={rawData} />

        {/* Section header for expense categories */}
        <div className="pt-4">
          <h2 className="text-white/60 text-sm font-medium mb-4">Udgifter i % af omsætning</h2>
        </div>

        {/* Expense categories grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {expenseCategories.map((cat) => (
            <CategoryCard
              key={cat.dataKey}
              title={cat.title}
              dataKey={cat.dataKey}
              rawData={rawData}
              color={cat.color}
              icon={cat.icon}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
